import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarController,
  LineController,
  BarElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import './css/MonitoramentoPublico.css';
import { API_BASE_URL } from '../config/ApiConfig';
import { formatCurrencyView } from './utils/currencyUtils';
import { formatCNPJ } from './utils/cnpjUtils';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarController,
  LineController,
  BarElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend
);

const CORES = {
  receitas: 'rgba(25, 135, 84, .75)',
  receitasBorda: '#198754',
  rendimentos: 'rgba(13, 148, 136, .75)',
  rendimentosBorda: '#0d9488',
  despesas: 'rgba(220, 53, 69, .7)',
  despesasBorda: '#dc3545',
  saldoAcumulado: '#0d6efd',
};

const opcoesCompactas = {
  locale: 'pt-BR',
  notation: 'compact',
  compactDisplay: 'short',
};

function MonitoramentoPublico() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [associacoes, setAssociacoes] = useState([]);
  const [cnpjSelecionado, setCnpjSelecionado] = useState('');
  const [anosDisponiveis, setAnosDisponiveis] = useState([]);
  const [anoSelecionado, setAnoSelecionado] = useState('');
  const [cidadeFiltro, setCidadeFiltro] = useState('');
  const [resumo, setResumo] = useState(null);
  const [carregandoAssociacoes, setCarregandoAssociacoes] = useState(true);
  const [carregandoResumo, setCarregandoResumo] = useState(false);
  const [mensagemErro, setMensagemErro] = useState('');

  // Carrega associações públicas e aplica deep-link (?cnpj=&ano=)
  useEffect(() => {
    let ativo = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL.TRANSPARENCIA_PUBLICA}/associacoes`);
        if (!ativo) return;
        setAssociacoes(data);

        const cnpjUrl = searchParams.get('cnpj');
        const cidadeUrl = searchParams.get('cidade');
        const valido = data.find(a => a.cnpj === cnpjUrl);
        let cidadeEfetiva = cidadeUrl || '';
        if (cidadeUrl && data.some(a => chaveCidade(a) === cidadeUrl)) {
          cidadeEfetiva = cidadeUrl;
        } else if (valido) {
          cidadeEfetiva = chaveCidade(valido);
        }
        if (cidadeEfetiva) setCidadeFiltro(cidadeEfetiva);

        if (valido) {
          selecionarAssociacao(valido.cnpj, true, cidadeEfetiva);
        } else if (data.length === 1 && cidadeEfetiva) {
          selecionarAssociacao(data[0].cnpj, true, cidadeEfetiva);
        }
      } catch (e) {
        if (ativo) {
          setMensagemErro('Não foi possível carregar a lista de associações. Tente novamente mais tarde.');
        }
      } finally {
        if (ativo) setCarregandoAssociacoes(false);
      }
    })();
    return () => {
      ativo = false;
    };
  }, []);

  async function selecionarAssociacao(cnpj, deepLink = false, cidadeDeepLink) {
    const cidadeParaLink = deepLink ? (cidadeDeepLink || '') : cidadeFiltro;
    setCnpjSelecionado(cnpj);
    setResumo(null);
    setAnosDisponiveis([]);
    setMensagemErro('');
    atualizarUrl(cidadeParaLink, cnpj, null);
    try {
      const { data: anos } = await axios.get(`${API_BASE_URL.TRANSPARENCIA_PUBLICA}/anos`, {
        params: { cnpj },
      });
      if (!Array.isArray(anos) || anos.length === 0) {
        setMensagemErro('Esta associação ainda não possui movimentos financeiros registrados.');
        return;
      }
      setAnosDisponiveis(anos);
      const anoUrl = Number(searchParams.get('ano'));
      const anoInicial = deepLink && anos.includes(anoUrl) ? anoUrl : anos[0];
      await carregarResumo(cnpj, anoInicial);
    } catch (e) {
      tratarErroPublico(e);
    }
  }

  async function trocarAno(ano) {
    setAnoSelecionado(ano);
    atualizarUrl(cidadeFiltro, cnpjSelecionado, ano);
    await carregarResumo(cnpjSelecionado, ano);
  }

  async function carregarResumo(cnpj, ano) {
    setCarregandoResumo(true);
    setMensagemErro('');
    try {
      const { data } = await axios.get(`${API_BASE_URL.TRANSPARENCIA_PUBLICA}/resumo`, {
        params: { cnpj, ano },
      });
      setResumo(data);
      setAnoSelecionado(ano);
    } catch (e) {
      setResumo(null);
      tratarErroPublico(e);
    } finally {
      setCarregandoResumo(false);
    }
  }

  function tratarErroPublico(e) {
    const status = e?.response?.status;
    const msg = e?.response?.data?.message;
    if (status === 404 || status === 400) {
      setMensagemErro(msg || 'Associação não disponível no portal da transparência.');
    } else {
      setMensagemErro('Não foi possível carregar os dados. Tente novamente mais tarde.');
    }
  }

  function atualizarUrl(cidade, cnpj, ano) {
    const proximos = {};
    if (cidade) proximos.cidade = cidade;
    if (cnpj) proximos.cnpj = cnpj;
    if (ano) proximos.ano = String(ano);
    setSearchParams(proximos, { replace: true });
  }

  const chaveCidade = a =>
    a.logrCidade ? (a.logrUf ? `${a.logrCidade} - ${a.logrUf}` : a.logrCidade) : '';

  const cidadesDisponiveis = useMemo(() => {
    const cidades = new Set();
    associacoes.forEach(a => {
      const chave = chaveCidade(a);
      if (chave) cidades.add(chave);
    });
    return Array.from(cidades).sort((x, y) => x.localeCompare(y, 'pt-BR'));
  }, [associacoes]);

  const associacoesFiltradas = useMemo(() => {
    if (!cidadeFiltro) return associacoes;
    return associacoes.filter(a => chaveCidade(a) === cidadeFiltro);
  }, [associacoes, cidadeFiltro]);

  function trocarCidade(cidade) {
    setCidadeFiltro(cidade);
    if (!cnpjSelecionado) {
      atualizarUrl(cidade, null, null);
      return;
    }
    const atual = associacoes.find(a => a.cnpj === cnpjSelecionado);
    if (cidade && chaveCidade(atual || {}) !== cidade) {
      setCnpjSelecionado('');
      setAnosDisponiveis([]);
      setAnoSelecionado('');
      setResumo(null);
      setMensagemErro('');
      atualizarUrl(cidade, null, null);
    } else {
      atualizarUrl(cidade, cnpjSelecionado, anoSelecionado);
    }
  }

  const dadosGrafico = useMemo(() => {
    if (!resumo) return null;
    const agora = new Date();
    const limite = Number(resumo.ano) === agora.getFullYear()
      ? agora.getMonth() + 1
      : resumo.meses.length;
    const mesesVisiveis = resumo.meses.slice(0, limite);
    const labels = mesesVisiveis.map(m => m.mes.substring(0, 3));
    return {
      labels,
      datasets: [
        {
          type: 'line',
          label: 'Saldo acumulado',
          data: mesesVisiveis.map(m => Number(m.saldoAcumulado)),
          borderColor: CORES.saldoAcumulado,
          backgroundColor: CORES.saldoAcumulado,
          borderWidth: 2,
          pointRadius: 3,
          tension: .35,
          order: 0,
        },
        {
          type: 'bar',
          label: 'Receitas',
          data: mesesVisiveis.map(m => Number(m.receitas)),
          backgroundColor: CORES.receitas,
          borderColor: CORES.receitasBorda,
          borderWidth: 1,
          borderRadius: 4,
          order: 1,
        },
        {
          type: 'bar',
          label: 'Rendimentos',
          data: mesesVisiveis.map(m => Number(m.rendimentos)),
          backgroundColor: CORES.rendimentos,
          borderColor: CORES.rendimentosBorda,
          borderWidth: 1,
          borderRadius: 4,
          order: 2,
        },
        {
          type: 'bar',
          label: 'Despesas',
          data: mesesVisiveis.map(m => Number(m.despesas)),
          backgroundColor: CORES.despesas,
          borderColor: CORES.despesasBorda,
          borderWidth: 1,
          borderRadius: 4,
          order: 3,
        },
      ],
    };
  }, [resumo]);

  const opcoesGrafico = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { position: 'bottom', labels: { usePointStyle: true, boxWidth: 8 } },
      tooltip: {
        callbacks: {
          label: contexto =>
            ` ${contexto.dataset.label}: ${formatCurrencyView(contexto.parsed.y)}`,
        },
      },
    },
    scales: {
      y: {
        ticks: {
          callback: valor => valor.toLocaleString('pt-BR', opcoesCompactas),
        },
      },
    },
  }), []);

  return (
    <div className="transp-page">
      <header className="transp-header">
        <div className="container">
          <div className="transp-brand">
            <div className="transp-brand-icon">
              <i className="bi bi-bank"></i>
            </div>
            <div>
              <h1 className="transp-title">Portal de Monitoramento</h1>
              <p className="transp-subtitle">Prestação de contas pública das associações</p>
            </div>
          </div>
          <button type="button" className="transp-btn-acessar" onClick={() => navigate('/')}>
            <i className="bi bi-box-arrow-in-right"></i>
            Acessar o sistema
          </button>
        </div>
      </header>

      <main className="transp-main">
        <div className="container">

          <div className="card transp-selectors mb-4">
            <div className="card-body py-3">
              <div className="row g-3 align-items-end">
                <div className="col-md-3">
                  <label htmlFor="sel-cidade" className="form-label fw-semibold small text-muted mb-1">
                    Cidade
                  </label>
                  <select
                    id="sel-cidade"
                    className="form-select"
                    value={cidadeFiltro}
                    onChange={e => trocarCidade(e.target.value)}
                    disabled={cidadesDisponiveis.length === 0}
                  >
                    <option value="">Todas as cidades</option>
                    {cidadesDisponiveis.map(cidade => (
                      <option key={cidade} value={cidade}>{cidade}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-5">
                  <label htmlFor="sel-associacao" className="form-label fw-semibold small text-muted mb-1">
                    Associação
                  </label>
                  <select
                    id="sel-associacao"
                    className="form-select"
                    value={cnpjSelecionado}
                    onChange={e => selecionarAssociacao(e.target.value)}
                    disabled={!cidadeFiltro || carregandoAssociacoes || associacoes.length === 0}
                  >
                    {!cidadeFiltro && (
                      <option value="">Selecione uma cidade primeiro</option>
                    )}
                    {cidadeFiltro && associacoes.length === 0 && !carregandoAssociacoes && (
                      <option value="">Nenhuma associação disponível</option>
                    )}
                    {cidadeFiltro && associacoesFiltradas.length === 0 && associacoes.length > 0 && (
                      <option value="">Nenhuma associação nesta cidade</option>
                    )}
                    {carregandoAssociacoes && <option value="">Carregando...</option>}
                    {cidadeFiltro && associacoesFiltradas.length > 0 && cnpjSelecionado === '' && (
                      <option value="">Selecione uma associação</option>
                    )}
                    {cidadeFiltro && associacoesFiltradas.map(a => (
                      <option key={a.cnpj} value={a.cnpj}>
                        {a.nome}
                      </option>
                    ))}
                  </select>
                </div>
                {resumo && (
                  <div className="col-md-2">
                    <label htmlFor="inp-cnpj" className="form-label fw-semibold small text-muted mb-1">
                      CNPJ
                    </label>
                    <input
                      id="inp-cnpj"
                      type="text"
                      className="form-control"
                      value={formatCNPJ(resumo.cnpj)}
                      readOnly
                    />
                  </div>
                )}
                <div className="col-md-2">
                  <label htmlFor="sel-ano" className="form-label fw-semibold small text-muted mb-1">
                    Exercício
                  </label>
                  <select
                    id="sel-ano"
                    className="form-select"
                    value={anoSelecionado}
                    onChange={e => trocarAno(Number(e.target.value))}
                    disabled={anosDisponiveis.length === 0 || carregandoResumo}
                  >
                    {anosDisponiveis.length === 0 && <option value="">—</option>}
                    {anosDisponiveis.map(ano => (
                      <option key={ano} value={ano}>{ano}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {mensagemErro && (
            <div className="alert alert-warning d-flex align-items-center gap-2 shadow-sm border-0 rounded-3" role="alert">
              <i className="bi bi-info-circle-fill fs-5"></i>
              <div>{mensagemErro}</div>
            </div>
          )}

          {!cnpjSelecionado && !mensagemErro && associacoes.length === 0 && !carregandoAssociacoes && (
            <div className="text-center text-muted py-5">
              <i className="bi bi-building-slash fs-1 d-block mb-3 opacity-50"></i>
              <p className="mb-1">Nenhuma associação publicou dados neste portal até o momento.</p>
              <p className="small">As associações aparecem aqui quando habilitam a transparência pública.</p>
            </div>
          )}

          {carregandoResumo && (
            <div className="text-center py-5">
              <div className="spinner-border text-success" role="status">
                <span className="visually-hidden">Carregando...</span>
              </div>
              <p className="text-muted mt-3 mb-0">Carregando prestação de contas...</p>
            </div>
          )}

          {resumo && !carregandoResumo && (
            <>
              <h5 className="fw-bold mb-3">
                {resumo.nomeAssociacao}{' '}
                <span className="badge bg-success-subtle text-success-emphasis ms-1">
                  Exercício {resumo.ano}
                </span>
              </h5>

              <div className="row g-3 mb-4">
                <TotalCard icone="bi-arrow-down-circle" cor="receitas" rotulo="Receitas" valor={resumo.totalReceitas} />
                <TotalCard icone="bi-graph-up-arrow" cor="rendimentos" rotulo="Rendimentos" valor={resumo.totalRendimentos} />
                <TotalCard icone="bi-arrow-up-circle" cor="despesas" rotulo="Despesas" valor={resumo.totalDespesas} />
                <TotalCard icone="bi-cash-stack" cor="saldo" rotulo="Saldo acumulado" valor={resumo.saldoAcumuladoFinal} />
              </div>
              <div className="card transp-section mb-4">
                <div className="card-body">
                  <div className="transp-section-title">
                    <i className="bi bi-bar-chart-line"></i>
                    Movimento financeiro mensal
                  </div>
                  <div style={{ height: 300 }}>
                    {dadosGrafico && <Bar data={dadosGrafico} options={opcoesGrafico} />}
                  </div>
                </div>
              </div>

              <div className="row g-4 mb-4">
                <div className="col-lg-7">
                  <div className="card transp-section h-100">
                    <div className="card-body">
                      <div className="transp-section-title">
                        <i className="bi bi-calendar3"></i>
                        Detalhamento por mês
                      </div>
                      <div className="table-responsive-sm">
                        <table className="table table-hover table-sm transp-table mb-0">
                          <thead>
                            <tr>
                              <th>Mês</th>
                              <th className="text-end">Rendimentos</th>
                              <th className="text-end">Receitas</th>
                              <th className="text-end">Despesas</th>
                              <th className="text-end">Saldo</th>
                              <th className="text-end">Saldo acumulado</th>
                            </tr>
                          </thead>
                          <tbody>
                            {resumo.meses.filter(linhaTemMovimento).map((m, i) => (
                              <tr key={m.mes}>
                                <td>{m.mes}</td>
                                <td className="text-end text-num">{formatCurrencyView(m.rendimentos)}</td>
                                <td className="text-end text-num">{formatCurrencyView(m.receitas)}</td>
                                <td className="text-end text-num">{formatCurrencyView(m.despesas)}</td>
                                <td className={`text-end text-num ${Number(m.saldo) < 0 ? 'text-danger' : ''}`}>
                                  {formatCurrencyView(m.saldo)}
                                </td>
                                <td className={`text-end text-num fw-semibold ${Number(m.saldoAcumulado) < 0 ? 'text-danger' : ''}`}>
                                  {formatCurrencyView(m.saldoAcumulado)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="table-light fw-bold">
                              <td>Total</td>
                              <td className="text-end text-num">{formatCurrencyView(resumo.totalRendimentos)}</td>
                              <td className="text-end text-num">{formatCurrencyView(resumo.totalReceitas)}</td>
                              <td className="text-end text-num">{formatCurrencyView(resumo.totalDespesas)}</td>
                              <td colSpan="2" className="text-end text-num">
                                {formatCurrencyView(resumo.saldoAcumuladoFinal)}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                      {resumo.meses.every(m => !linhaTemMovimento(m)) && (
                        <p className="text-muted small mt-2 mb-0">Nenhum movimento registrado neste exercício.</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="col-lg-5 d-flex flex-column gap-4">
                  <RankingCard
                    titulo="Despesas por categoria"
                    icone="bi-tags"
                    itens={resumo.despesasPorCategoria}
                    vazio="Nenhuma despesa paga neste exercício."
                  />
                  <RankingCard
                    titulo="Receitas por programa"
                    icone="bi-diagram-3"
                    itens={resumo.receitasPorPrograma}
                    vazio="Nenhuma receita registrada neste exercício."
                  />
                </div>
              </div>
            </>
          )}

        </div>
      </main>

      <footer className="transp-footer">
        <div className="container">
          <div className="d-flex flex-column flex-sm-row justify-content-between gap-2">
            <div>
              <i className="bi bi-shield-check me-1"></i>
              Dados apresentados de forma agregada, sem informações pessoais de favorecidos.
            </div>
            <div>Portal de Monitoramento · Prestação de contas</div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function linhaTemMovimento(mes) {
  return ['rendimentos', 'receitas', 'despesas'].some(
    campo => Number(mes[campo]) !== 0
  );
}

const CORES_ICONE = {
  receitas: '#198754',
  rendimentos: '#0d9488',
  despesas: '#dc3545',
  saldo: '#0d6efd',
};

function TotalCard({ icone, cor, rotulo, valor }) {
  return (
    <div className="col-sm-6 col-lg-3">
      <div className={`card transp-card-total transp-card-${cor}`}>
        <div className="card-body py-3">
          <div className="d-flex justify-content-between align-items-center">
            <span className="transp-label">{rotulo}</span>
            <i className={`bi ${icone}`} style={{ color: CORES_ICONE[cor], fontSize: '1.2rem' }}></i>
          </div>
          <div className={`transp-value ${Number(valor) < 0 ? 'text-danger' : ''}`}>
            {formatCurrencyView(valor)}
          </div>
        </div>
      </div>
    </div>
  );
}

function RankingCard({ titulo, icone, itens, vazio }) {
  const maximo = itens && itens.length > 0
    ? Math.max(...itens.map(i => Number(i.valor)))
    : 0;

  return (
    <div className="card transp-section flex-grow-1">
      <div className="card-body">
        <div className="transp-section-title">
          <i className={`bi ${icone}`}></i>
          {titulo}
        </div>
        {!itens || itens.length === 0 ? (
          <p className="text-muted small mb-0">{vazio}</p>
        ) : (
          <ul className="list-unstyled mb-0">
            {itens.map(item => {
              const percentual = maximo > 0 ? (Number(item.valor) / maximo) * 100 : 0;
              return (
                <li key={item.descricao} className="mb-3 last-no-margin">
                  <div className="d-flex justify-content-between align-items-baseline gap-2 mb-1">
                    <span className="small fw-semibold text-truncate" title={item.descricao}>
                      {item.descricao}
                    </span>
                    <span className="small text-nowrap fw-semibold">{formatCurrencyView(item.valor)}</span>
                  </div>
                  <div className="progress" style={{ height: 6 }}>
                    <div
                      className="progress-bar bg-success"
                      role="progressbar"
                      style={{ width: `${percentual}%` }}
                      aria-valuenow={percentual}
                      aria-valuemin="0"
                      aria-valuemax="100"
                    ></div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

export default MonitoramentoPublico;
